import axios from "axios";
import { logEvent, logApiError } from "../utils/datadogLogger";
import store from "../store/store";
import { logout } from "../features/auth/user.reducer";
import {
  buildAuthHeaders,
  getStoredJwtToken,
  normalizeRawToken,
} from "./authHeaders";

const getHeaderValue = (headers, key) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    return headers.get(key) || headers.get(key.toLowerCase()) || "";
  }

  const matchingKey = Object.keys(headers).find(
    (headerKey) => headerKey.toLowerCase() === key.toLowerCase()
  );
  return matchingKey ? headers[matchingKey] : "";
};

const SESSION_HEADER_NAMES = new Set(["jwt", "x-jwt-token"]);

const toPlainHeaders = (headers) => {
  if (!headers) return {};
  if (typeof headers.toJSON === "function") {
    return headers.toJSON();
  }
  return { ...headers };
};

export const getRequestJwtToken = (config = {}) => {
  const headers = config?.headers;
  const requestToken =
    getHeaderValue(headers, "X-JWT-TOKEN") || getHeaderValue(headers, "jwt");
  return normalizeRawToken(String(requestToken || ""));
};

export const mergeAuthHeaders = (headers = {}, jwtToken = "") => {
  const plainHeaders = toPlainHeaders(headers);
  const sanitizedHeaders = Object.keys(plainHeaders).reduce((acc, key) => {
    if (!SESSION_HEADER_NAMES.has(key.toLowerCase())) {
      acc[key] = plainHeaders[key];
    }
    return acc;
  }, {});

  return {
    ...sanitizedHeaders,
    ...buildAuthHeaders(jwtToken || getStoredJwtToken()),
  };
};

export const shouldLogoutForAuthFailure = (config = {}, activeToken = "") => {
  const currentToken = normalizeRawToken(activeToken || getStoredJwtToken());
  const requestToken = getRequestJwtToken(config);

  if (!currentToken || !requestToken) return false;
  return currentToken === requestToken;
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 10000,
  timeoutErrorMessage:
    "Request taking longer than expected. Check your network",
  headers: {
    "Content-Type": "application/json",
    ...buildAuthHeaders(),
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getStoredJwtToken();
    config.headers = mergeAuthHeaders(config.headers, token);

// Log request start with body if present (exact Android format)
    const userName = store.getState().user.user?.name || 'Test user';
    const userEmail = store.getState().user.user?.email || 'unknown';
    const fullUrl = config.baseURL + config.url;
    const paramsStr = JSON.stringify(config.data || config.params || {}, (key, value) => {
      // Mask sensitive fields
      if (key === 'jwtToken' || key === 'password' || key === 'token') return '[MASKED]';
      return value;
    });
    
    const requestLogMessage = `User: ${userName} Email: ${userEmail} ::: API call to ${fullUrl} with params: ${paramsStr}`;
    
    logEvent({
      event: requestLogMessage,
      flow: 'api',
      metadata: {
        method: config.method?.toUpperCase(),
        url: fullUrl,
        request_body: config.data,
        request_params: config.params,
      },
    });

    return config;
  },
  (error) => {
    logEvent({
      event: 'api_request_error',
      level: 'error',
      flow: 'api',
      metadata: { error: error.message },
    });
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful API response with body (exact Android format)
    const userName = store.getState().user.user?.name || 'Test user';
    const userEmail = store.getState().user.user?.email || 'unknown';
    const fullUrl = response.config.baseURL + response.config.url;
    const paramsStr = JSON.stringify(response.config.data || response.config.params || {}, (key, value) => {
      if (key === 'jwtToken' || key === 'password' || key === 'token') return '[MASKED]';
      return value;
    });
    const responseStr = JSON.stringify(response.data, (key, value) => {
      if (key === 'password' || key === 'jwtoken') return '[MASKED]';
      return value;
    });
    
    const successLogMessage = `User: ${userName} Email: ${userEmail} ::: API call to ${fullUrl} with params: ${paramsStr} Returned status code ${response.status} with response: ${responseStr}`;
    
    logEvent({
      event: successLogMessage,
      flow: 'api',
      metadata: {
        method: response.config.method?.toUpperCase(),
        url: fullUrl,
        status: response.status,
        response_time_ms: response.duration || Date.now() - (response.config.metadata?.startTime || 0),
        response_body: response.data,
      },
    });

    if (response.data?.status === "Fail") {
      const statusCode = String(response?.data?.status_code ?? "");
      const message = String(response?.data?.message ?? "").toLowerCase();
      
      // Log business logic failure with response body (exact Android format)
      const userName = store.getState().user.user?.name || 'Test user';
      const userEmail = store.getState().user.user?.email || 'unknown';
      const fullUrl = response.config.baseURL + response.config.url;
      const paramsStr = JSON.stringify(response.config.data || response.config.params || {}, (key, value) => {
        if (key === 'jwtToken' || key === 'password' || key === 'token') return '[MASKED]';
        return value;
      });
      const responseStr = JSON.stringify(response.data, (key, value) => {
        if (key === 'password' || key === 'jwtoken') return '[MASKED]';
        return value;
      });
      
      const errorLogMessage = `User: ${userName} Email: ${userEmail} ::: API call to ${fullUrl} with params: ${paramsStr} Returned status code ${response.status} with response: ${responseStr}`;
      
      logEvent({
        event: errorLogMessage,
        level: 'error',
        flow: 'api',
        metadata: {
          method: response.config.method?.toUpperCase(),
          url: fullUrl,
          status: response.status,
          status_code: statusCode,
          message,
          response_body: response.data,
          requires_logout: (statusCode === "304" || message.includes("jwt token is not valid")) && shouldLogoutForAuthFailure(response.config),
        },
      });

      if (
        (statusCode === "304" ||
          message.includes("jwt token is not valid")) &&
        shouldLogoutForAuthFailure(response.config)
      ) {
        store.dispatch(logout());
      }

      const error = new Error();
      error.response = {
        ...response,
        data: response.data,
        status: 400,
      };
      return Promise.reject(error);
    }
    return response;
  },
  (error) => {
    // Log network/HTTP errors with response body if available (exact Android format)
    const userName = store.getState().user.user?.name || 'Test user';
    const userEmail = store.getState().user.user?.email || 'unknown';
    const fullUrl = error.config ? (error.config.baseURL + error.config.url) : 'unknown';
    const paramsStr = error.config ? JSON.stringify(error.config.data || error.config.params || {}, (key, value) => {
      if (key === 'jwtToken' || key === 'password' || key === 'token') return '[MASKED]';
      return value;
    }) : '{}';
    const status = error.response?.status || 0;
    const responseStr = JSON.stringify(error.response?.data || {}, (key, value) => {
      if (key === 'password' || key === 'jwtoken') return '[MASKED]';
      return value;
    });
    
    const networkErrorMessage = `User: ${userName} Email: ${userEmail} ::: API call to ${fullUrl} with params: ${paramsStr} Returned status code ${status} with response: ${responseStr}`;
    
    logEvent({
      event: networkErrorMessage,
      level: 'error',
      flow: 'api',
      metadata: {
        method: error.config?.method?.toUpperCase(),
        url: fullUrl,
        status: status,
        status_text: error.response?.statusText,
        response_body: error.response?.data,
        error_message: error.message,
        requires_logout: error.response?.status === 401 && shouldLogoutForAuthFailure(error.config),
      },
    });

    if (
      error.response?.status === 401 &&
      shouldLogoutForAuthFailure(error.config)
    ) {
      store.dispatch(logout());
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
